import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ShieldCheck, Loader2, MapPin, 
  Smartphone, Banknote, CheckCircle2, ArrowRight, Lock, Wallet, AlertCircle, Coins
} from 'lucide-react';
import SEO from '../components/SEO';

const Checkout = () => {
  const { cartItems, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [address, setAddress] = useState(user?.address || "");
  const [phone, setPhone] = useState(user?.phone || "");

  // 🔥 Strict Wallet Logic
  const walletBalance = user?.walletBalance || 0; 
  const isInsufficientWallet = paymentMethod === "WALLET" && subtotal > walletBalance;

  const RAZORPAY_KEY = "rzp_test_S843KIKhoXFdmW";

  useEffect(() => {
    if (user) {
      if (user.address) setAddress(user.address);
      if (user.phone) setPhone(user.phone);
    }
  }, [user]);

  const saveOrderToDB = async (paymentId = null) => {
  try {
    // 1. Token nikalne ka sahi tarika (Context se check karo)
    const token = user?.accessToken || user?.token;

    if (!token) {
      alert("Session Expired: Please login again.");
      return null;
    }

    setLoading(true);

    const orderData = {
      userId: user._id,
      userEmail: user.email,
      products: cartItems.map(item => ({
        productId: item._id || item.id,
        quantity: Number(item.quantity) || 1,
        size: item.selectedSize || 'OS',
        title: item.name || item.title,
        price: Number(item.price),
        img: item.img || item.image
      })),
      amount: Number(subtotal),
      address: address || "No Address Provided",
      phone: phone || "No Phone Provided",
      paymentId: paymentId || (paymentMethod === "COD" ? "COD_STUB" : "WALLET_TRX"),
      payment_method: paymentMethod
    };

    // 2. Headers mein "token" keyword hi use karo (Backend verifyToken yahi mang raha hai)
    const res = await axios.post("https://wethrift-backend.onrender.com/api/orders", orderData, {
  headers: { 
    token: `Bearer ${user.accessToken}`,
    Authorization: `Bearer ${user.accessToken}`
  }
});

    return res.data;

  } catch (err) {
    console.error("Order Error:", err.response?.data);
    alert(`Error: ${err.response?.data?.message || "Forbidden Access"}`);
    return null;
  } finally {
    setLoading(false);
  }
};

  const handleRazorpayPayment = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("https://wethrift-backend.onrender.com/api/payment/checkout", { amount: subtotal });
      const options = {
        key: RAZORPAY_KEY,
        amount: data.order.amount,
        currency: data.order.currency,
        name: "WE THRIFT ARCHIVE",
        order_id: data.order.id,
        handler: async (response) => {
          const verifyRes = await axios.post("https://wethrift-backend.onrender.com/api/payment/paymentverification", response);
          if (verifyRes.data.success) {
            const responseData = await saveOrderToDB(response.razorpay_payment_id);
            if (responseData?.order) processSuccess(responseData.order);
          }
        },
        prefill: { contact: phone, email: user?.email },
        theme: { color: "#10b981" }
      };
      new window.Razorpay(options).open();
    } catch (err) {
      alert("Payment Initiation Failed");
    } finally {
      setLoading(false);
    }
  };

  const processSuccess = (order) => {
    localStorage.setItem("last_order_id", order._id);
    navigate("/success", { state: { orderId: order._id, order: order }, replace: true });
    setTimeout(() => clearCart(), 500);
  };

  const handleMainOrderAction = async () => {
    if (paymentMethod === "COD" || paymentMethod === "WALLET") {
      const responseData = await saveOrderToDB();
      if (responseData?.success) processSuccess(responseData.order);
    } else {
      await handleRazorpayPayment();
    }
  };

  return (
    <>
    <SEO title="Secure Checkout | WE THRIFT" />

    <div className={`min-h-screen pt-32 pb-20 px-4 ${isDarkMode ? 'bg-black text-white' : 'bg-white text-black'}`}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* LEFT: Checkout Steps */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} className="text-emerald-500" />
            <h1 className="text-2xl font-[1000] italic uppercase tracking-tighter">Secure_Checkout_Gate</h1>
          </div>

          {[1, 2, 3].map((s) => (
            <div key={s} className={`border rounded-2xl transition-all duration-500 ${isDarkMode ? 'bg-zinc-950/50 border-white/5' : 'bg-zinc-50 border-black/5'} ${step === s ? 'ring-1 ring-emerald-500/50' : ''}`}>
              <div className="p-6 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-4">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black transition-colors ${step >= s ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-500'}`}>{s}</span>
                  <h2 className="font-black uppercase tracking-widest text-[11px]">{s === 1 ? 'Identity' : s === 2 ? 'Logistics' : 'Payment'}</h2>
                </div>
                {step > s && <button onClick={() => setStep(s)} className="text-[10px] font-black uppercase text-emerald-500 hover:underline">Edit_Node</button>}
              </div>

              {step === s && (
                <div className="p-8 animate-in slide-in-from-bottom-2 duration-500">
                  {s === 1 && (
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Authenticated_As:</p>
                        <p className="font-black uppercase tracking-tighter text-lg">{user?.username} <span className="text-zinc-600 mx-2">//</span> {user?.email}</p>
                      </div>
                      <button onClick={() => setStep(2)} className="bg-white text-black px-8 py-3 rounded-full font-black text-[10px] uppercase hover:bg-emerald-500 transition-colors">Continue_Protocol</button>
                    </div>
                  )}
                  {s === 2 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5 focus-within:border-emerald-500/50 transition-colors">
                          <p className="text-[9px] text-zinc-500 font-black uppercase mb-2">Destination_Node:</p>
                          <input className="bg-transparent border-none outline-none w-full font-bold uppercase text-sm" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Enter delivery address" />
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5 focus-within:border-emerald-500/50 transition-colors">
                          <p className="text-[9px] text-zinc-500 font-black uppercase mb-2">Comms_Line:</p>
                          <input className="bg-transparent border-none outline-none w-full font-bold uppercase text-sm" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" />
                        </div>
                      </div>
                      <button onClick={() => setStep(3)} className="bg-white text-black px-8 py-3 rounded-full font-black text-[10px] uppercase hover:bg-emerald-500 transition-colors">Confirm_Logistics</button>
                    </div>
                  )}
                  {s === 3 && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { id: "DIGITAL", icon: Smartphone, label: "ONLINE" },
                        { id: "WALLET", icon: Wallet, label: "WALLET" },
                        { id: "COD", icon: Banknote, label: "CASH" }
                      ].map((m) => (
                        <button key={m.id} onClick={() => setPaymentMethod(m.id)} className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 group ${paymentMethod === m.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/5 hover:border-white/10'}`}>
                          <m.icon size={24} className={paymentMethod === m.id ? 'text-emerald-500' : 'text-zinc-500 group-hover:text-white'} />
                          <span className={`text-[10px] font-black tracking-widest ${paymentMethod === m.id ? 'text-white' : 'text-zinc-500'}`}>{m.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* RIGHT: Cart Summary & Wallet Validation */}
        <div className="lg:col-span-4">
          <div className="bg-zinc-950 border border-white/5 p-8 rounded-[2.5rem] sticky top-32 shadow-2xl">
            <h3 className="text-[10px] font-black uppercase text-zinc-500 tracking-[0.3em] mb-6">Order_Manifest</h3>
            
            <div className="space-y-4 mb-8 max-h-[30vh] overflow-y-auto pr-2 custom-scrollbar">
              {cartItems.map((item, i) => (
                <div key={i} className="flex gap-4 items-center bg-white/5 p-3 rounded-2xl border border-white/5">
                  <img src={item.img || item.image} className="w-12 h-14 object-cover rounded-lg grayscale hover:grayscale-0 transition-all" alt="" crossOrigin="anonymous" />
                  <div className="flex-1 overflow-hidden">
                    <p className="text-[10px] font-black uppercase truncate">{item.name}</p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[8px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded font-black uppercase tracking-tighter">Size_{item.selectedSize}</span>
                      <p className="text-sm font-black italic text-emerald-500">₹{item.price}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="space-y-4 pt-6 border-t border-white/10">
              <div className="flex justify-between items-end">
                <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Settlement_Total</p>
                <p className="text-3xl font-[1000] italic tracking-tighter text-white">₹{subtotal}</p>
              </div>
              
              {/* Wallet Status Box */}
              <div className={`p-4 rounded-2xl border transition-all duration-500 ${isInsufficientWallet ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <Coins size={14} className="text-emerald-500" />
                    <span className="text-[9px] font-black uppercase text-zinc-500">Asset_Balance</span>
                  </div>
                  <span className="text-sm font-black italic">🪙 {walletBalance}</span>
                </div>
                
                {paymentMethod === "WALLET" && (
                  isInsufficientWallet ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-red-500">
                        <AlertCircle size={12} />
                        <p className="text-[9px] font-black uppercase tracking-tighter">Insufficient Funds: -{subtotal - walletBalance} Coins</p>
                      </div>
                      <Link to="/refer" className="flex items-center justify-center gap-2 w-full py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-[9px] font-black uppercase text-red-500 hover:bg-red-500 hover:text-white transition-all">
                        Refill_Wallet <ArrowRight size={10} />
                      </Link>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-500">
                      <CheckCircle2 size={12} />
                      <p className="text-[9px] font-black uppercase tracking-tighter">Protocol_Authorized</p>
                    </div>
                  )
                )}
              </div>

              <button 
                onClick={handleMainOrderAction} 
                disabled={loading || step < 3 || isInsufficientWallet}
                className="w-full bg-white text-black py-5 rounded-full font-black uppercase text-[10px] tracking-[0.2em] mt-2 hover:bg-emerald-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed group relative overflow-hidden"
              >
                {loading ? <Loader2 className="animate-spin mx-auto" /> : 
                 isInsufficientWallet ? "Access_Denied" : `Execute_${paymentMethod}_Protocol`}
              </button>
              
              <p className="text-[8px] text-zinc-600 text-center font-bold uppercase tracking-[0.2em]">End-to-End Encryption Active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Checkout;